<script setup lang="ts">
import { useAuthUser } from '@/helpers/globals'
import { errToStr } from '@/helpers/tools'
import type { Adresse } from '@/models/Adresse'
import type { ApollonConfFk } from '@/models/ApollonConfFk'
import { useToast } from '@/composables/toast'
import StepKanton from '@/views/auth/gesuch/CreateStepKanton.vue'
import StepFK from '@/views/auth/gesuch/CreateStepFoerderkategorie.vue'
import StepRolle from '@/views/auth/gesuch/CreateStepRolle.vue'
import StepOrganisation from '@/views/auth/gesuch/CreateStepOrganisation.vue'
import StepBestaetigung from '@/views/auth/gesuch/CreateStepBestaetigung.vue'
import ProfileDialog from '@/components/complete-profile-first-dlg.vue'
import { computed, onMounted, ref, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { Kanton } from '@/models/Kanton'
import { instance as getApi } from '@/helpers/api'
import KpToolbar from '@/components/kp-toolbar.vue'

const step = ref(1)
const kanton: Ref<Kanton | null> = ref(null)
const apollonConfFk: Ref<ApollonConfFk[]> = ref([])
const loading = ref(false)
const rolle = ref(null)
const organisation: Ref<Adresse | null> = ref(null)
const showProfileDialog = ref(false)

const { t } = useI18n()
const router = useRouter()

const steps = computed(() => {
    return rolle.value && rolle.value === 'kontakt'
        ? [
              { value: 1, text: t('choose_kanton') },
              { value: 2, text: t('choose_fk') },
              { value: 3, text: t('choose_role') },
              { value: 4, text: t('confirm') },
          ]
        : [
              { value: 1, text: t('choose_kanton') },
              { value: 2, text: t('choose_fk') },
              { value: 3, text: t('choose_role') },
              { value: 4, text: t('choose_org') },
              { value: 5, text: t('confirm') },
          ]
})

onMounted(() => {
    if (!useAuthUser()?.profileComplete()) {
        showProfileDialog.value = true
    }
})

async function onCreateGesuchBtn() {
    loading.value = true
    try {
        if (Array.isArray(apollonConfFk.value) && apollonConfFk.value.length !== 1) {
            throw new Error('Förderkategorie konnte nicht ermittelt werden')
        }
        const data = await getApi().post('/gesuch/create', {
            rolle: rolle.value,
            org_id: organisation.value?.id,
            apollon_conf_fk_id: apollonConfFk.value[0].id,
        })
        const gesuchNummer = data.data.nummer
        loading.value = false
        useToast().add({ text: t('gesuch_erstellt', { nr: gesuchNummer }) })
        router.push({ name: 'gesuch', params: { nr: gesuchNummer } })
    } catch (e) {
        useToast().add({ text: errToStr(e), color: 'error' })
    } finally {
        loading.value = false
    }
}
function cancelProfileDialog() {
    showProfileDialog.value = false
    router.go(-1)
}
function closeProfileDialog() {
    showProfileDialog.value = false
}
</script>

<template>
    <div>
        <KpToolbar :title="$t('gesuch_erstellen')" icon="fas fa-balance-scale" />
        <v-container class="kp-large-container" fluid>
            <v-stepper v-model="step">
                <template v-slot:default="{ prev, next }">
                    <v-stepper-header>
                        <template v-for="n in steps" :key="`${n}-step`">
                            <v-stepper-item
                                :complete="step > n.value"
                                :step="n.text"
                                :value="n.value"
                            >
                                <template v-slot:title
                                    ><div class="hidden-xs">{{ n.text }}</div></template
                                ></v-stepper-item
                            >
                        </template>
                    </v-stepper-header>

                    <v-stepper-window>
                        <v-stepper-window-item :value="1">
                            <StepKanton v-model:model-value="kanton" @next="next" />
                        </v-stepper-window-item>
                        <v-stepper-window-item :value="2">
                            <StepFK
                                v-model:model-value="apollonConfFk"
                                :kanton="kanton"
                                @next="next"
                                @back="prev"
                            />
                        </v-stepper-window-item>
                        <v-stepper-window-item :value="3">
                            <StepRolle
                                v-model:model-value="rolle"
                                :apollon-conf-fk="apollonConfFk"
                                @next="next"
                                @back="prev"
                            />
                        </v-stepper-window-item>
                        <v-stepper-window-item :value="4" v-if="rolle && rolle !== 'kontakt'">
                            <StepOrganisation
                                v-model="organisation"
                                :rolle="rolle"
                                @next="next"
                                @back="prev"
                            />
                        </v-stepper-window-item>
                        <v-stepper-window-item :value="5">
                            <StepBestaetigung
                                :loading="loading"
                                :kanton="kanton"
                                :apollon-conf-fk="apollonConfFk"
                                :rolle="rolle"
                                :organisation="organisation"
                                @create="onCreateGesuchBtn"
                                @back="prev"
                            />
                        </v-stepper-window-item>
                    </v-stepper-window>
                </template>
            </v-stepper>
        </v-container>
        <v-overlay absolute :value="loading">
            <v-progress-circular indeterminate />
        </v-overlay>
        <ProfileDialog
            v-model="showProfileDialog"
            @close="closeProfileDialog"
            @cancel="cancelProfileDialog"
        />
    </div>
</template>
